export interface AudienceSubscriptionBatchPayload {
  audienceIds: number[];
  userSession: string;
  workspaceId: number;
  userId: string;
}

interface AudienceSubscriptionPayload {
  audienceId: number;
  userSession: string;
  workspaceId: number;
  userId: string;
}

interface AudienceSubscriptionEmitter {
  emit: (
    event: 'subscribe_audiences_batch' | 'subscribe_audience',
    payload: AudienceSubscriptionBatchPayload | AudienceSubscriptionPayload,
  ) => unknown;
}

interface EmitAudienceSubscriptionBatchOptions {
  socket: AudienceSubscriptionEmitter;
  audienceIds: Iterable<string | number>;
  trackedAudienceIds: Set<number>;
  userSession: string;
  workspaceId: number;
  userId: string;
  includeTracked?: boolean;
}

/**
 * Track and subscribe to several audience rooms with one backend authorization query.
 * Existing tracked IDs are skipped during normal subscription, but reconnects can
 * explicitly include them because Socket.IO room membership is lost on disconnect.
 */
export const emitAudienceSubscriptionBatch = ({
  socket,
  audienceIds,
  trackedAudienceIds,
  userSession,
  workspaceId,
  userId,
  includeTracked = false,
}: EmitAudienceSubscriptionBatchOptions): number[] => {
  const normalizedAudienceIds = [...new Set(
    Array.from(audienceIds)
      .map((audienceId) => (typeof audienceId === 'string' ? parseInt(audienceId, 10) : audienceId))
      .filter((audienceId) => Number.isInteger(audienceId) && audienceId > 0),
  )];

  const audienceIdsToSubscribe = includeTracked
    ? normalizedAudienceIds
    : normalizedAudienceIds.filter((audienceId) => !trackedAudienceIds.has(audienceId));

  if (audienceIdsToSubscribe.length === 0) return [];

  audienceIdsToSubscribe.forEach((audienceId) => trackedAudienceIds.add(audienceId));
  socket.emit('subscribe_audiences_batch', {
    audienceIds: audienceIdsToSubscribe,
    userSession,
    workspaceId,
    userId,
  });

  return audienceIdsToSubscribe;
};

interface RetryDeniedAudienceSubscriptionBatchOptions {
  socket: AudienceSubscriptionEmitter;
  audienceIds: Iterable<number>;
  trackedAudienceIds: Set<number>;
  userSession: string;
  workspaceId: number;
  userId: string;
}

/**
 * Retry a denied all-or-nothing batch one audience at a time. The backend can
 * then accept valid rooms independently and report the stale ID that failed.
 */
export const retryDeniedAudienceSubscriptionBatch = ({
  socket,
  audienceIds,
  trackedAudienceIds,
  userSession,
  workspaceId,
  userId,
}: RetryDeniedAudienceSubscriptionBatchOptions): number[] => {
  const audienceIdsToRetry = [...new Set(audienceIds)];

  audienceIdsToRetry.forEach((audienceId) => trackedAudienceIds.delete(audienceId));
  audienceIdsToRetry.forEach((audienceId) => {
    trackedAudienceIds.add(audienceId);
    socket.emit('subscribe_audience', {
      audienceId,
      userSession,
      workspaceId,
      userId,
    });
  });

  return audienceIdsToRetry;
};

/** Remove the one stale audience reported by an individual room denial. */
export const removeDeniedAudienceSubscription = (
  trackedAudienceIds: Set<number>,
  room: string,
): number | null => {
  const match = room.match(/^audience:(\d+)$/);
  if (!match) return null;

  const audienceId = Number(match[1]);
  trackedAudienceIds.delete(audienceId);
  return audienceId;
};
